import * as React from "react";
import axios from "axios";
import { SearchBar } from "./Navigator";
import { DataTable, shortHash } from "./ui";

interface IBlock {
  height: number;
  time: number;
  hash: string;
  tx: any;
  difficulty?: number;
}

interface IStatus {
  demo?: boolean;
  blocks?: number;
  difficulty?: number;
  networkhashps?: number;
  subversion?: string;
  chain?: string;
  algo?: string;
  mempool?: number;
  connections?: number;
}

function fmtHashrate(n?: number) {
  if (!n && n !== 0) return "—";
  if (n >= 1e12) return (n / 1e12).toFixed(2) + " TH/s";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + " GH/s";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + " MH/s";
  if (n >= 1e3) return (n / 1e3).toFixed(2) + " kH/s";
  return n.toFixed(0) + " H/s";
}

export function Blocks() {
  const [blocks, setBlocks] = React.useState<IBlock[]>([]);
  const [status, setStatus] = React.useState<IStatus>({});
  const [mempool, setMempool] = React.useState<any>({});

  React.useEffect(() => {
    async function work() {
      const [b, s, m] = await Promise.all([
        axios.get("/api/blocks"),
        axios.get("/api/status").catch(() => ({ data: {} })),
        axios.get("/api/mempool").catch(() => ({ data: {} })),
      ]);
      setBlocks(b.data);
      setStatus(s.data || {});
      setMempool(m.data || {});
      if (b.data?.[0]?.height) {
        document.title = "Telestai · " + b.data[0].height.toLocaleString();
      }
    }
    work();
    const interval = setInterval(work, 20000);
    return () => clearInterval(interval);
  }, []);

  const mempoolCount = Array.isArray(mempool)
    ? mempool.length
    : Object.keys(mempool || {}).length;

  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <div className="kicker">Telestai Core 3.0.0</div>
          <h1>The network, in the open</h1>
          <p>
            Asset-aware explorer for TLS — blocks, transactions, and native
            assets on Meraki.
          </p>
          <SearchBar />
        </div>
      </section>

      <div className="gold-band">
        <div className="wrap">
          <div className="stat">
            <div className="label">Height</div>
            <div className="value">
              {(status.blocks ?? blocks[0]?.height ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="stat">
            <div className="label">Algo</div>
            <div className="value">{status.algo || "Meraki"}</div>
          </div>
          <div className="stat">
            <div className="label">Difficulty</div>
            <div className="value">
              {status.difficulty != null
                ? Number(status.difficulty).toFixed(3)
                : "—"}
            </div>
          </div>
          <div className="stat">
            <div className="label">Hashrate</div>
            <div className="value">{fmtHashrate(status.networkhashps)}</div>
          </div>
          <div className="stat">
            <div className="label">Mempool</div>
            <div className="value">{(status.mempool ?? mempoolCount).toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div className="page">
        {status.demo ? (
          <div className="banner">
            Showing sample TestNet 3.0.0 data — address/asset indexes are not
            enabled on the node, or RPC is unreachable. Live tip still used
            when available.
          </div>
        ) : null}

        <div className="section-head">
          <h2>Latest blocks</h2>
          <a href="index.html?route=BLOCKS">All blocks →</a>
        </div>
        <div className="card">
          <DataTable
            headers={["Height", "Time", "Transactions", "Hash"]}
            rows={blocks.map((block) => {
              const url = "index.html?route=BLOCK&hash=" + block.hash;
              const time = new Date(block.time * 1000).toLocaleString();
              const txc = Array.isArray(block.tx) ? block.tx.length : block.tx;
              return [
                <a href={url}>{block.height.toLocaleString()}</a>,
                time,
                txc,
                <a className="mono" href={url}>
                  {shortHash(block.hash)}
                </a>,
              ];
            })}
          />
        </div>
      </div>
    </>
  );
}
